import Button from '@app/components/Button';
import ArrowForwardIcon from '@app/components/Icons/ArrowForwardIcon';
import EmailIcon from '@app/components/Icons/EmailIcon';
import Mobile from '@app/components/Icons/Mobile';
import H2 from '@app/components/typography/H2';
import FormGroup from '@app/forms/components/FormGroup';
import { LoginContacts, LoginStep, SendTo } from '@app/pages/auth/Login/Login';
import { button, buttonGroup, emailButton, heading, hint, phoneNumber } from '@app/pages/auth/Login/Step2.sx';
import { useStep2 } from '@app/pages/auth/Login/useStep2';
import { codeEmail } from '@app/utils/format/codeEmail';
import { codePhoneNumber } from '@app/utils/format/codePhoneNumber';
import { formatPhoneNumber } from '@app/utils/format/phoneNumber';
import { Box } from '@mui/material';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  contacts: LoginContacts;
  setContacts: Dispatch<SetStateAction<LoginContacts>>;
  setStep: Dispatch<SetStateAction<LoginStep>>;
  sendTo: SendTo;
  setSendTo: Dispatch<SetStateAction<SendTo>>;
}

const Step2 = ({ contacts, setContacts, setStep, sendTo, setSendTo }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { email, phone } = contacts;
  const { isLoading, send } = useStep2(sendTo, setSendTo, email, phone, setStep, setContacts);

  return (
    <Box>
      <H2 sx={heading}>
        Please select the appropriate option to receive a verification code to confirm entry from a new device.
      </H2>

      <Box sx={buttonGroup}>
        <Button
          sx={button}
          variant="outlined"
          color={sendTo === 'phone' ? 'primary' : 'inherit'}
          startIcon={<Mobile />}
          disabled={!phone}
          onClick={() => setSendTo('phone')}
        >
          Phone
        </Button>
        <Button
          sx={{ ...button, ...emailButton }}
          variant="outlined"
          color={sendTo === 'email' ? 'primary' : 'inherit'}
          startIcon={<EmailIcon />}
          onClick={() => setSendTo('email')}
        >
          Email
        </Button>
      </Box>

      {sendTo === 'email' && <Box sx={hint}>Email message to {codeEmail(email)}</Box>}

      {sendTo === 'phone' && (
        <Box sx={hint}>
          Send text message with passcode to{' '}
          <Box sx={phoneNumber} component="span">
            {codePhoneNumber(formatPhoneNumber(phone))}
          </Box>
        </Box>
      )}

      <FormGroup>
        <Button
          type="submit"
          loading={isLoading}
          disabled={!sendTo}
          endIcon={!isLoading && <ArrowForwardIcon />}
          onClick={send}
        >
          {!isLoading && t('Send')}
        </Button>
      </FormGroup>
    </Box>
  );
};

export default Step2;
